import React, { useState } from 'react';
import { Box, IconButton, Typography, Menu, MenuItem, ClickAwayListener } from '@mui/material';
import { Close as CloseIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';

interface TagProps {
  label: string;
  onDelete?: () => void;
  color?: string;
  backgroundColor?: string;
  options?: string[];
  onSelect?: (option: string) => void;
  isInteractive?: boolean;
  disabled?: boolean;
}

export default function Tag({
  label,
  onDelete,
  color = '#000000',
  backgroundColor = '#E0E0E0',
  options = [],
  onSelect,
  isInteractive = false,
  disabled = false
}: TagProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isInteractive) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionSelect = (option: string) => {
    if (disabled) {
      return;
    }

    if (onSelect) {
      onSelect(option);
    }
    handleClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <Box
          onClick={handleClick}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor,
            borderRadius: '16px',
            padding: '4px 8px',
            margin: '4px',
            cursor: isInteractive && !disabled ? 'pointer' : 'default',
            '&:hover': {
              backgroundColor: isInteractive && !disabled ? `${backgroundColor}DD` : backgroundColor,
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color,
              fontSize: '0.875rem',
              fontWeight: 500,
              marginRight: onDelete || isInteractive ? '4px' : 0
            }}
          >
            {label}
          </Typography>
          {isInteractive && !disabled && <ArrowDropDownIcon sx={{ fontSize: '1rem', color }} />}
          {onDelete && !disabled && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              sx={{
                padding: '2px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <CloseIcon sx={{ fontSize: '1rem', color }} />
            </IconButton>
          )}
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option}
              onClick={() => handleOptionSelect(option)}
              sx={{
                fontSize: '0.875rem',
                padding: '8px 16px'
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </ClickAwayListener>
  );
}
